import {QueryItemBuilder} from '../api_requests/builders/query_item_builder';
import {ApplicationRequestConfig} from '../api_requests/application_constants';

export enum SortField {
  RELEVANCE = 'relevance',
  NAME = 'name',
  UPDATED = 'updated',
  NUMBER_VIEWS = 'numberViews',
  NUMBER_LIKES = 'numberLikes',
}

export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc'
}

export interface SearchSortItem {
  field: SortField;
  direction?: SortDirection;
}

export enum ApiEnvironment {
  PROD = 'prod',
  PRE = 'pre',
  TEST = 'test',
  DEV = 'dev',
  LOCAL = 'local'
}

export interface SearchApiFilter {
  field: string;
  values: string[];
}

export interface SearchStandardFields {
  files?: string[];
  links?: string[];
  userAccessViewer?: string[];
  userAccessOwner?: string[];
  userAccessEditor?: string[];
  userAccessScope?: number[];
  userAccessSchools?: string[];
  originalId?: string[];
  created?: string[];
  modified?: string[];
  published?: boolean[];
  status?: number[];
  isResourceCollection?: boolean[];
}

export interface SearchApiSearchOptions {
  searchDraft?: boolean;
  searchDraftAndDocument?: boolean;
  searchAllIncludingArchive?: boolean;
  noDocument?: boolean;
  noDraft?: boolean;
  noArchive?: boolean;
  noCollections?: boolean;
  semantic?: boolean;
  searchFields?: string[];
  hasAccess?: boolean;
  onlyMine?: boolean;
  onlySharedWithMe?: boolean;
  onlySharedByMe?: boolean;
  userId?: string;
  userSchools?: string[];
  searchScope?: number;
  size?: number;
  page?: number;
}

export interface SearchApiRequestConfig {
  includeApplicationRequestConfig?: boolean;
  application: ApplicationRequestConfig;
  keyword?: string;
  filters?: SearchApiFilter[];
  sort?: SearchSortItem;
  options?: SearchApiSearchOptions;
  standardFieldFilters?: SearchStandardFields;
  customQuery?: any;
  axiosMethod?: string;
  itemId?: string;
  apiEnv?: ApiEnvironment;
  isHomePage?: boolean;
}

export enum SearchQueryParams {
  KEYWORD = 'keyword',
  SIZE = 'size',
  PAGE = 'page',
  SORT = 'sort',
  DRAFT = 'searchDraft',
  BOTH_DRAFT_DOCUMENT = 'searchDraftAndDocument',
  ALL_INCLUDE_ARCHIVE = 'searchAllIncludingArchive',
  NO_DOCUMENT = 'noDocument',
  NO_DRAFT = 'noDraft',
  NO_ARCHIVE = 'noArchive',
  NO_COLLECTIONS = 'noCollections',
  SEMANTIC_SEARCH = 'semantic',
  CUSTOM_SEARCH_FIELDS = 'searchFields',
  FILES = 'files.path',
  LINKS = 'links.path',
  USERID = 'audit.sub',
  RECORD_SOURCE = 'record.collection',
  APPLICATION_SEARCH = 'audit.iss',
  OWNER = 'userAccess.owner',
  EDITOR = 'userAccess.editor',
  VIEWER = 'userAccess.viewer',
  SCHOOLS = 'userAccess.schools',
  SCOPE = 'userAccess.scope',
  ORIGINAL_ID = 'record.originalId',
  CREATED = 'record.created',
  MODIFIED = 'record.modified',
  PUBLISHED = 'record.published',
  STATUS = 'record.status',
  RESOURCE_COLLECTION = 'record.isResourceCollection',
  HAS_ACCESS = 'hasAccess',
  ONLY_MINE = 'onlyMine',
  ONLY_SHARED = 'onlyShared',
  ItemID = 'ItemID',
  IS_HOMEPAGE = 'isHomePage',
  //** Hub Filters - START **
  Year = 'Year',
  KLA = 'KLA',
  Source = 'Source',
  RandNTopic = 'RandNTopic',
  Format = 'Format',
  ResourceType = 'ResourceType', // Hub-Type, checkboxHubType
  Phase = 'Phase.id',
  SEFAlignment = 'SEFAlignment.id',
  FocusArea = 'FocusArea.id',
  ResourceCategory = 'ResourceCategory',
  //** Hub Filters - END **
}

export class SearchBuilder {
  static standardFieldBuilder(searchStandardFields: SearchStandardFields): string {
    let s = '';
    s += QueryItemBuilder.arrayProp(searchStandardFields.created, SearchQueryParams.CREATED);
    s += QueryItemBuilder.arrayProp(searchStandardFields.published, SearchQueryParams.PUBLISHED);
    s += QueryItemBuilder.arrayProp(searchStandardFields.originalId, SearchQueryParams.ORIGINAL_ID);
    s += QueryItemBuilder.arrayProp(searchStandardFields.modified, SearchQueryParams.MODIFIED);
    s += QueryItemBuilder.arrayProp(searchStandardFields.links, SearchQueryParams.LINKS);
    s += QueryItemBuilder.arrayProp(searchStandardFields.files, SearchQueryParams.FILES);
    s += QueryItemBuilder.arrayProp(searchStandardFields.userAccessEditor, SearchQueryParams.EDITOR);
    s += QueryItemBuilder.arrayProp(searchStandardFields.userAccessOwner, SearchQueryParams.OWNER);
    s += QueryItemBuilder.arrayProp(searchStandardFields.userAccessScope, SearchQueryParams.SCOPE);
    s += QueryItemBuilder.arrayProp(searchStandardFields.userAccessSchools, SearchQueryParams.SCHOOLS);
    s += QueryItemBuilder.arrayProp(searchStandardFields.userAccessViewer, SearchQueryParams.VIEWER);
    s += QueryItemBuilder.arrayProp(searchStandardFields.status, SearchQueryParams.STATUS);
    s += QueryItemBuilder.arrayProp(searchStandardFields.isResourceCollection, SearchQueryParams.RESOURCE_COLLECTION);
    return s;
  }

  static sortBuilder(searchSortItem: SearchSortItem): string {
    if (searchSortItem) {
      if (searchSortItem.direction) {
        return SearchQueryParams.SORT + `=${searchSortItem.field}:${searchSortItem.direction}&`;
      } else {
        return SearchQueryParams.SORT + `=${searchSortItem.field}&`;
      }
    }
    return '';
  }

  static optionsBuilder(config: SearchApiSearchOptions): string {
    let s = '';
    s += QueryItemBuilder.booleanProp(config.searchDraft, SearchQueryParams.DRAFT);
    s += QueryItemBuilder.booleanProp(config.noArchive, SearchQueryParams.NO_ARCHIVE);
    s += QueryItemBuilder.booleanProp(config.noCollections, SearchQueryParams.NO_COLLECTIONS);
    s += QueryItemBuilder.booleanProp(config.semantic, SearchQueryParams.SEMANTIC_SEARCH);
    s += QueryItemBuilder.booleanProp(config.noDocument, SearchQueryParams.NO_DOCUMENT);
    s += QueryItemBuilder.booleanProp(config.noDraft, SearchQueryParams.NO_DRAFT);
    s += QueryItemBuilder.booleanProp(config.searchAllIncludingArchive, SearchQueryParams.ALL_INCLUDE_ARCHIVE);
    s += QueryItemBuilder.booleanProp(config.searchDraftAndDocument, SearchQueryParams.BOTH_DRAFT_DOCUMENT);
    // s += QueryItemBuilder.booleanProp(config.hasAccess, SearchQueryParams.HAS_ACCESS);
    // s += QueryItemBuilder.booleanProp(config.onlyMine, SearchQueryParams.ONLY_MINE);
    // s += QueryItemBuilder.booleanProp(config.onlyShared, SearchQueryParams.ONLY_SHARED);
    s += QueryItemBuilder.arrayProp(config.searchFields, SearchQueryParams.CUSTOM_SEARCH_FIELDS);
    s += QueryItemBuilder.valueProp(config.page, SearchQueryParams.PAGE);
    s += QueryItemBuilder.valueProp(config.size, SearchQueryParams.SIZE);
    return s;
  }

  static itemIdBuilder(itemId: string): string {
    if (itemId) {
      return `${SearchQueryParams.ItemID}=${itemId}&`;
    }
    return '';
  }
}
